import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export const SCOPES = [
  { key: 'mine', label: 'Your threats', hint: 'CVEs cited by the threats matched to your organization.' },
  { key: 'kev', label: 'Exploited (CISA KEV)', hint: "Every CVE on CISA's Known Exploited Vulnerabilities list: attackers are using these now." },
  { key: 'new', label: 'New (30 days)', hint: 'High and Critical CVEs NVD published in the last 30 days.' },
  { key: 'trending', label: 'Trending', hint: 'CVEs whose exploitation likelihood (EPSS) rose this week, or that CISA added to its exploited list in the last two weeks.' },
  { key: 'all', label: 'Everything', hint: 'All of the above in one list.' },
];

export const SORT_LABELS = {
  priority: 'Priority',
  newest: 'Newest published',
  trending: 'Trending',
  kev_added: 'Recently added to CISA KEV',
  epss: 'Exploitation likelihood (EPSS)',
  cvss: 'CVSS score',
  cited: 'Most cited in your threats',
};

export const SEVERITIES = ['Critical', 'High', 'Medium', 'Low'];
export const PRIORITIES = ['Act', 'Attend', 'Track*', 'Track'];

export const TOGGLES = [
  { key: 'kev', param: 'kev', label: 'Exploited in the wild', facet: 'kev', hint: 'On the CISA Known Exploited Vulnerabilities list' },
  { key: 'ransomware', param: 'rw', label: 'Ransomware use', facet: 'ransomware', hint: 'CISA reports use in ransomware campaigns' },
  { key: 'exploit', param: 'x', label: 'Public exploit', facet: 'exploit', hint: 'Public exploit code exists (Exploit-DB)' },
  { key: 'nuclei', param: 'nu', label: 'Scanner template', facet: 'nuclei', hint: 'A Nuclei detection template exists, so you can scan for it' },
  { key: 'in_stack', param: 'stack', label: 'In my stack', facet: 'in_stack', hint: 'Matches a technology you declared in your profile' },
  { key: 'has_hunt', param: 'hunt', label: 'Has hunting queries', facet: 'has_hunt', hint: 'Threat hunting queries are ready' },
  { key: 'cited', param: 'cited', label: 'Cited in my threats', facet: 'cited', hint: 'Mentioned by a threat matched to you' },
];

export const DAYS = [
  { value: '', label: 'Any time' }, { value: '7', label: 'Last 7 days' }, { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' }, { value: '365', label: 'Last year' },
];

const list = (v) => (v ? v.split(',').filter(Boolean) : []);

/** The Vulnerabilities page's filters, kept in the URL so a view can be shared and survives a reload. */
export default function useVulnFilters() {
  const [params, setParams] = useSearchParams();
  const raw = params.toString();

  const filters = useMemo(() => {
    const p = new URLSearchParams(raw);
    const scope = SCOPES.some((s) => s.key === p.get('scope')) ? p.get('scope') : 'mine';
    const f = {
      scope, sort: SORT_LABELS[p.get('sort')] ? p.get('sort') : null, q: p.get('q') || '',
      priority: list(p.get('pri')), severity: list(p.get('sev')), days: p.get('days') || '', vendor: p.get('vendor') || '',
      cve: p.get('cve') || '', tab: p.get('tab') || '',
    };
    TOGGLES.forEach((t) => { f[t.key] = p.get(t.param) === '1'; });
    return f;
  }, [raw]);

  const set = useCallback((patch, { keepPanel = true } = {}) => {
    setParams((cur) => {
      const next = new URLSearchParams(cur);
      const put = (k, v) => { if (v === '' || v == null || v === false || (Array.isArray(v) && !v.length)) next.delete(k); else next.set(k, Array.isArray(v) ? v.join(',') : v === true ? '1' : String(v)); };
      Object.entries(patch).forEach(([key, value]) => {
        const toggle = TOGGLES.find((t) => t.key === key);
        const name = toggle ? toggle.param : ({ priority: 'pri', severity: 'sev' }[key] || key);
        put(name, value);
      });
      if (!keepPanel) { next.delete('cve'); next.delete('tab'); }
      return next;
    }, { replace: true });
  }, [setParams]);

  const clear = useCallback(() => set({
    q: '', priority: [], severity: [], days: '', vendor: '', ...Object.fromEntries(TOGGLES.map((t) => [t.key, false])),
  }), [set]);

  const active = filters.q || filters.priority.length || filters.severity.length || filters.days || filters.vendor || TOGGLES.some((t) => filters[t.key]);

  // What the API receives: the filters, never the panel.
  const query = useMemo(() => {
    const q = { scope: filters.scope };
    if (filters.sort) q.sort = filters.sort;
    if (filters.q.trim()) q.q = filters.q.trim();
    if (filters.priority.length) q.priority = filters.priority.join(',');
    if (filters.severity.length) q.severity = filters.severity.join(',');
    if (filters.days && filters.scope !== 'new') q.published_days = filters.days;
    if (filters.vendor) q.vendor = filters.vendor;
    TOGGLES.forEach((t) => { if (filters[t.key]) q[t.key] = true; });
    return q;
  }, [filters]);

  return { filters, set, clear, active: Boolean(active), query };
}
